import { ChangeDetectionStrategy, Component, input, output, signal, WritableSignal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { EntityStatus, FileAdminFilterKey, FilePurpose, ZoudashUtils } from '@workspace/data-types';
import {
  AbstractSignalFormComponent,
  DataHeaderFilter,
  FilterChangedDetection,
  FilterRecord,
} from '@workspace/ng-aura';
import { FilePurposeDisplayPipe, StateStatusDisplayPipe } from '@workspace/ng-data-access';
import { FileColumnKey } from '../abstract-files-data-table.component';

export interface FileFilterModel {
  mimeType: string | string[] | null;
  purpose: string | string[] | null;
  state: string | string[] | null;
}

@Component({
  selector: 'core-file-data-table-filter',
  templateUrl: './file-data-table-filter.component.html',
  imports: [
    FormField,
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    MatSlideToggleModule,
    FilePurposeDisplayPipe,
    StateStatusDisplayPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileDataTableFilterCoreComponent extends AbstractSignalFormComponent<
  Partial<Record<FileAdminFilterKey, string | string[]>>,
  FileFilterModel
> {
  currentFilterView = input<string | null>(null);
  filters = input.required<DataHeaderFilter<FileColumnKey>[]>();
  filterChanged = output<FilterChangedDetection>();
  filePurposeValues = Object.values(FilePurpose);
  stateValues = Object.values(EntityStatus);
  fileAdminFilterKey = FileAdminFilterKey;
  mimePrefixes = ['application', 'audio', 'image', 'video'];

  override model: WritableSignal<FileFilterModel> = signal<FileFilterModel>({
    mimeType: null,
    purpose: null,
    state: null,
  });

  override formTree = form(this.model);

  override emitDto(newDto: Partial<Record<FileAdminFilterKey, string | string[]>>): void {
    this.filterChanged.emit({
      filter: newDto as FilterRecord,
      changed: !ZoudashUtils.isEqual(newDto, this.ogValue),
    });
  }

  protected override buildModelFromDto(
    dto: Partial<Record<FileAdminFilterKey, string | string[]>>,
  ): FileFilterModel {
    return {
      mimeType: dto?.[FileAdminFilterKey.Mimetype] ?? null,
      purpose: dto?.[FileAdminFilterKey.Purpose] ?? null,
      state: dto?.[FileAdminFilterKey.State] ?? null,
    };
  }

  protected override buildDtoFromModel(
    model: FileFilterModel,
  ): Partial<Record<FileAdminFilterKey, string | string[]>> {
    const dto: Partial<Record<FileAdminFilterKey, string | string[]>> = {
      mimeType: model.mimeType ?? undefined,
      purpose: model.purpose ?? undefined,
      'state.status': model.state ?? undefined,
    };
    return Object.fromEntries(Object.entries(dto).filter(([_, v]) => v != null)) as Partial<
      Record<FileAdminFilterKey, string | string[]>
    >;
  }
}
